import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconAlertTriangle } from "@tabler/icons-react"
import { useState } from "react"
import type { InquiryRegulationOption } from "../../../api/supplierInquiriesApi"
import {
  inquiryRegulationLabels,
  picksPfasWithoutAnnexXvii,
  REGULATION_REGION_ORDER,
  regulationRegion,
  toggleRegulationKey,
  type RegulationRegion,
} from "../../../utils/inquiryScope"

const REGION_LABEL_KEY: Record<RegulationRegion, TranslationKeys> = {
  eu: "MC_ORG_REG_REGION_EU",
  cn: "MC_ORG_REG_REGION_CN",
  us: "MC_ORG_REG_REGION_US",
  other: "MC_ORG_REG_REGION_OTHER",
}

interface InquiryRegulationPickerProps {
  /** Every regulation the org's inquiries may offer, in catalogue order. */
  options: readonly InquiryRegulationOption[]
  /** The ticked keys. */
  value: readonly string[]
  onChange: (keys: string[]) => void
  /** Visibly off (e.g. while the inquiry is being sent). */
  disabled?: boolean
  /** A change is still saving: clicks wait quietly, with no "not allowed" look,
   *  so a quick second click cannot overtake the first save. */
  busy?: boolean
  /** Set while the selection is a hand pick: offers going back to Settings. */
  onFollowSettings?: () => void
  /** The draft's format does not ask PFAS at all: its Annex XVII warning would be
   *  moot there (the composer's "not asked" note already names PFAS). */
  pfasNotAsked?: boolean
  /** Couples PFAS to REACH Annex XVII as the declaration editor does (TUR-1733):
   *  ticking PFAS ticks XVII, unticking XVII unticks PFAS. */
  coupled?: boolean
  /** Why a greyed-out regulation cannot be picked, in the caller's words. */
  notPickableHint?: TranslationKeys
  /** Shown while nothing is ticked, in the caller's words. */
  emptyHint?: TranslationKeys
}

/** A short warning line under the chips. */
function PickerNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2"
    >
      <IconAlertTriangle size={14} className="mt-px flex-none text-warning" />
      <span className="text-[12px] text-white">{children}</span>
    </div>
  )
}

/**
 * The regulations one supplier inquiry asks (TUR-1730): toggle chips grouped by
 * region like Settings › Regelwerke, any pickable regulation tickable (also outside
 * the org's set), at least one kept. The groups sit in an accordion, collapsed to
 * one line naming what is ticked with a "Change" link; the notes below it stay
 * visible either way.
 * Nothing here depends on the attachment
 * format: a pick the format cannot cover is shown by the composer and refused at
 * send. PFAS without REACH Annex XVII is allowed, with a visible warning. Why a
 * regulation is greyed out shows on hover at once (and to screen readers); trying to drop the
 * last one says so below the chips rather than on a hover nobody sees.
 */
export function InquiryRegulationPicker({
  options,
  value,
  onChange,
  disabled,
  busy,
  onFollowSettings,
  pfasNotAsked,
  coupled,
  notPickableHint = "MC_INQ_REG_PICKER_NOT_PICKABLE",
  emptyHint = "MC_INQ_REG_PICKER_NONE_SET",
}: InquiryRegulationPickerProps) {
  const { translate } = useTranslation()
  // The selection on which the last ticked regulation was clicked: it stays, and a
  // note says why, until the selection changes (also from outside, e.g. a reset).
  const [keptLastOn, setKeptLastOn] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const selectionKey = value.join(",")
  const byKey = new Map(options.map((option) => [option.key, option]))
  // Only what the picker shows counts: a draft following Settings can carry a key it
  // doesn't offer (halogen-free, which the documents always ask), and counting that
  // hidden key let the last shown chip be unticked into an empty pick.
  const picked = new Set(value.filter((key) => byKey.has(key)))
  const labels = inquiryRegulationLabels(
    options.map((option) => option.key),
    translate,
  )
  const order = options.map((option) => option.key)

  function toggle(key: string) {
    // A change still saving holds clicks AND keys (pointer-events covers the mouse
    // only): a toggle on the not-yet-saved value would drop the first change.
    if (busy) return
    const next = new Set(
      coupled ? toggleRegulationKey(value, key) : [...picked],
    )
    if (!coupled) {
      if (next.has(key)) next.delete(key)
      else next.add(key)
    }
    if (next.size === 0) {
      setKeptLastOn(selectionKey)
      return
    }
    setKeptLastOn(null)
    onChange(order.filter((k) => next.has(k)))
  }

  const pickedLabels = labels
    .filter((regulation) => picked.has(regulation.key))
    .map((regulation) => regulation.label)

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn("flex flex-col gap-2", busy && "pointer-events-none")}
        aria-busy={busy || undefined}
      >
        <Accordion
          type="single"
          collapsible
          value={open ? "regulations" : ""}
          onValueChange={(item) => setOpen(item === "regulations")}
        >
          <AccordionItem value="regulations" className="border-none">
            {/* The yellow "Change" link and chevron say the line opens; plain grey
                text alone did not read as clickable. */}
            <AccordionTrigger
              data-testid="regulation-picker-trigger"
              className="group justify-start gap-1.5 py-1 text-[11.5px] font-normal text-grey5 hover:no-underline [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-yellow1"
            >
              <span className="text-left">
                {translate(
                  picked.size === 1
                    ? "MC_INQ_REG_PICKER_COUNT_ONE"
                    : "MC_INQ_REG_PICKER_COUNT",
                  { n: picked.size },
                )}
                {pickedLabels.length > 0 && (
                  <span className="text-white">
                    : {pickedLabels.join(", ")}
                  </span>
                )}
              </span>
              <span className="ml-1.5 shrink-0 font-medium text-yellow1 group-hover:underline">
                {translate(
                  open ? "MC_INQ_REG_PICKER_HIDE" : "MC_INQ_REG_PICKER_CHANGE",
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2 pt-1 pb-1">
              {REGULATION_REGION_ORDER.map((region) => {
                const regionLabels = labels.filter(
                  (regulation) => regulationRegion(regulation.key) === region,
                )
                if (regionLabels.length === 0) return null
                return (
                  <div
                    key={region}
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <span className="w-28 shrink-0 text-[11px] text-grey5">
                      {translate(REGION_LABEL_KEY[region])}
                    </span>
                    {regionLabels.map((regulation) => {
                      const on = picked.has(regulation.key)
                      const pickable =
                        byKey.get(regulation.key)?.pickable ?? false
                      const greyedOut = !pickable && !on
                      return (
                        <Tooltip key={regulation.key}>
                          <TooltipTrigger asChild>
                            {/* A span, so the hover works on a disabled chip too. */}
                            <span className="inline-flex">
                              <button
                                type="button"
                                aria-pressed={on}
                                aria-description={
                                  greyedOut
                                    ? translate(notPickableHint)
                                    : undefined
                                }
                                // A greyed-out chip stays focusable (aria-disabled, not
                                // disabled), so a keyboard reaches it and its tooltip says why.
                                aria-disabled={greyedOut || undefined}
                                disabled={disabled}
                                onClick={() => {
                                  if (!greyedOut) toggle(regulation.key)
                                }}
                                className={cn(
                                  // A disabled chip passes the pointer to its span, so the tooltip opens.
                                  "inline-flex h-[22px] items-center rounded-full border px-2 text-[11.5px] font-medium transition-colors disabled:pointer-events-none",
                                  on
                                    ? "border-yellow1 bg-yellow1/10 text-yellow1"
                                    : "border-dashed border-grey4 text-grey5 hover:text-white disabled:opacity-50 disabled:hover:text-grey5 aria-disabled:cursor-default aria-disabled:opacity-50 aria-disabled:hover:text-grey5",
                                )}
                              >
                                {regulation.label}
                              </button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {greyedOut
                              ? `${regulation.title} · ${translate(notPickableHint)}`
                              : regulation.title}
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </div>
                )
              })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {onFollowSettings && (
          <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-grey5">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (!busy) onFollowSettings()
              }}
              className="font-medium text-yellow1 hover:underline disabled:opacity-50"
            >
              {translate("MC_INQ_REG_PICKER_FOLLOW_SETTINGS")}
            </button>
          </div>
        )}
        {/* Only an empty org set leaves nothing ticked (a pick keeps one): the
            documents then ask everything they carry, as before Settings. */}
        {picked.size === 0 && (
          <span className="text-[11px] text-grey5">{translate(emptyHint)}</span>
        )}
        {keptLastOn === selectionKey && (
          <PickerNote>{translate("MC_INQ_REG_PICKER_MIN_ONE")}</PickerNote>
        )}
        {picksPfasWithoutAnnexXvii(value) && !pfasNotAsked && (
          <PickerNote>{translate("MC_INQ_REG_PICKER_PFAS_WARNING")}</PickerNote>
        )}
      </div>
    </TooltipProvider>
  )
}
